import {
  alpha,
  Box,
  Card,
  CardContent,
  Checkbox,
  Collapse,
  Grid,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import { ArrowDownward, ArrowUpward } from '@mui/icons-material';
import { useEffect, useState } from 'react';

export interface TrimOptionCardProps {
  option: {
    value: string;
    label: string;
    pricePerPound: number;
    minLbs: number;
    description?: string;
  };
  checked?: boolean;
  disabled?: boolean;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;

  // Additional props for expanded state
  selectionMode?: 'allowSelectionByWeight' | 'allowSelectionByPercentage';
  rank?: number;
  quantity?: number;
  totalSelected?: number;
  onRankUp?: () => void;
  onRankDown?: () => void;
  onQuantityChange?: (value: number) => void;
}

export function TrimOptionCard({
  option,
  checked,
  disabled,
  onChange,
  selectionMode = 'allowSelectionByWeight',
  rank,
  quantity,
  totalSelected = 0,
  onRankUp,
  onRankDown,
  onQuantityChange,
}: TrimOptionCardProps) {
  // Local state for input value to allow clearing while typing
  const [inputValue, setInputValue] = useState<string>('');

  // Sync input value with quantity prop
  useEffect(() => {
    if (quantity !== undefined && quantity !== null) {
      setInputValue(quantity.toString());
    } else {
      setInputValue('');
    }
  }, [quantity]);

  const handleCardClick = (e: React.MouseEvent) => {
    // Don't trigger card click if clicking on input fields or buttons
    const target = e.target as HTMLElement;
    if (
      target.tagName === 'INPUT' ||
      target.tagName === 'BUTTON' ||
      target.closest('button') ||
      target.closest('.MuiTextField-root')
    ) {
      return;
    }

    if (disabled || !onChange) return;

    const syntheticEvent = {
      target: {
        name: option.value,
        value: option.value,
        checked: !checked,
      },
    } as React.ChangeEvent<HTMLInputElement>;

    onChange(syntheticEvent);
  };

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);

  const handleQuantityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);

    if (onQuantityChange) {
      if (newValue === '') {
        onQuantityChange(0);
      } else {
        const parsed = parseFloat(newValue);
        if (!isNaN(parsed)) {
          onQuantityChange(parsed);
        }
      }
    }
  };

  const isWeightMode = selectionMode === 'allowSelectionByWeight';
  const isPercentageMode = selectionMode === 'allowSelectionByPercentage';

  return (
    <Card
      data-testid="trim-option-card"
      onClick={handleCardClick}
      sx={{
        borderRadius: '9px',
        my: 2,
        width: '100%',
        border: '2px solid', // Always 2px border to prevent layout shift
        borderColor: checked ? 'primary.main' : 'divider', // Divider color when not selected

        // selected state
        ...(checked
          ? {
              backgroundColor: (theme) =>
                alpha(theme.palette.primary.main, 0.1),
            }
          : {}),

        // hover (only when not disabled)
        ...(disabled
          ? { opacity: 0.7, cursor: 'not-allowed' }
          : {
              cursor: 'pointer',
              '&:hover': {
                borderColor: 'primary.main',
                backgroundColor: (theme) =>
                  checked
                    ? alpha(theme.palette.primary.main, 0.1)
                    : alpha(theme.palette.primary.main, 0.05),
              },
            }),

        // Ensure all child elements use pointer cursor when not disabled
        ...(!disabled && {
          '& *': {
            cursor: 'pointer !important',
          },
        }),
      }}
      variant="outlined"
    >
      <CardContent sx={{ '&:last-child': { pb: 2 } }}>
        <Grid container spacing={1} alignItems="center" wrap="nowrap">
          <Grid sx={{ display: 'flex', alignItems: 'center' }}>
            <Checkbox
              name={option.value}
              value={option.value}
              checked={checked}
              disabled={disabled}
              onChange={onChange}
              size="medium"
            />
          </Grid>
          <Grid sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              variant="subtitle1"
              sx={{
                color: 'text.primary',
                fontSize: '1.125rem',
                fontWeight: '500',
                lineHeight: '1.25rem',
                mb: option.description ? 0.5 : 0,
              }}
            >
              {option.label}
            </Typography>
            {option.description && (
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  fontSize: '0.875rem',
                  lineHeight: '1.25rem',
                }}
              >
                {option.description}
              </Typography>
            )}
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                fontSize: '0.875rem',
                lineHeight: '1.25rem',
                mt: option.description ? 0.5 : 0,
              }}
            >
              {formatCurrency(option.pricePerPound)} per lb • {option.minLbs}{' '}
              lbs minimum
            </Typography>
          </Grid>
        </Grid>

        {/* Expanded controls when selected */}
        <Collapse in={checked} timeout="auto" unmountOnExit>
          <Box
            sx={{
              mt: 2,
              pt: 2,
              borderTop: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 2,
                  alignItems: 'center',
                }}
              >
                {/* Priority/Rank Controls */}
                {totalSelected > 1 && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      flex: '1 1 auto',
                      minWidth: '200px',
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 500, minWidth: '70px' }}
                    >
                      Rank: {rank}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        onRankUp?.();
                      }}
                      disabled={rank === 1}
                      sx={{
                        padding: '4px',
                        '&:disabled': { opacity: 0.3 },
                      }}
                    >
                      <ArrowUpward fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        onRankDown?.();
                      }}
                      disabled={rank === totalSelected}
                      sx={{
                        padding: '4px',
                        '&:disabled': { opacity: 0.3 },
                      }}
                    >
                      <ArrowDownward fontSize="small" />
                    </IconButton>
                  </Box>
                )}

                {/* Quantity Input - Weight Mode */}
                {isWeightMode && (
                  <Box sx={{ flex: '1 1 auto', minWidth: '200px' }}>
                    <TextField
                      label="Quantity (lbs)"
                      type="number"
                      size="small"
                      value={inputValue}
                      onChange={handleQuantityChange}
                      onClick={(e) => e.stopPropagation()}
                      inputProps={{
                        min: 0,
                        step: 1,
                      }}
                      helperText={
                        option.minLbs > 0
                          ? `Minimum: ${option.minLbs} lbs`
                          : undefined
                      }
                      error={
                        typeof quantity === 'number' &&
                        quantity < (option.minLbs || 0) &&
                        quantity !== 0
                      }
                      fullWidth
                      sx={{
                        '& .MuiInputBase-root': {
                          backgroundColor: 'background.paper',
                        },
                      }}
                    />
                  </Box>
                )}

                {/* Percentage Input - Percentage Mode */}
                {isPercentageMode && (
                  <Box sx={{ flex: '1 1 auto', minWidth: '200px' }}>
                    <TextField
                      label="Percentage (%)"
                      type="number"
                      size="small"
                      value={inputValue}
                      onChange={handleQuantityChange}
                      onClick={(e) => e.stopPropagation()}
                      inputProps={{
                        min: 0,
                        max: 100,
                        step: 1,
                      }}
                      fullWidth
                      sx={{
                        '& .MuiInputBase-root': {
                          backgroundColor: 'background.paper',
                        },
                      }}
                    />
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
}
